import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, X, UserPlus } from "lucide-react";
import { PersonAvatar } from "@/components/PersonAvatar";
import { api, CATEGORY_OPTIONS, GL_OPTIONS } from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_GL = {
  Airfare: "6000", Meals: "6010", Software: "6020", Lodging: "6030",
  "Ground Transport": "6040", Office: "6050", Entertainment: "6060", Other: "6090",
};

const PAYMENT_METHODS = ["Corporate Visa", "Corporate Amex", "Corporate Mastercard", "Personal card (reimbursable)", "Cash (reimbursable)"];

const toYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const EMPTY = {
  merchant: "", amount: "", date: toYMD(new Date()), category: "Meals", employee_id: "", gl_code: "6010",
  location: "", payment_method: "", calendar_event: "", tax: "", tip: "", notes: "",
};

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="text-[10.5px] font-medium uppercase tracking-wide text-[#6b6b6b]">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export const CreateExpenseDialog = ({ open, onOpenChange, employees, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [attendees, setAttendees] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addAttendee = (id) => {
    const emp = employees.find((e) => e.id === id);
    if (emp && !attendees.includes(emp.name)) setAttendees((a) => [...a, emp.name]);
  };

  const submit = async () => {
    if (!form.merchant.trim() || !parseFloat(form.amount) || !form.employee_id) {
      toast.error("Merchant, amount and employee are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        tax: form.tax ? parseFloat(form.tax) : null,
        tip: form.tip ? parseFloat(form.tip) : null,
        location: form.location || null,
        payment_method: form.payment_method || null,
        calendar_event: form.calendar_event || null,
        notes: form.notes || null,
        attendees,
      };
      const r = await api.post("/expenses", payload);
      if (r.data.policy_status === "violation") toast.warning(`Filed & flagged — ${r.data.violation_reason}`);
      else if (r.data.auto_approved) toast.success("Created & auto-approved — within policy and threshold");
      else toast.success("Created — routed to review");
      setForm(EMPTY);
      setAttendees([]);
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the expense");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "h-9 border-[#2a2a2a] bg-[#161616] text-[13px] text-white placeholder:text-[#555]";
  const selectCls = "h-9 border-[#2a2a2a] bg-[#161616] text-[13px]";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto border-[#222] bg-[#141414] text-white" data-testid="create-expense-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px]">Create expense</DialogTitle>
          <DialogDescription className="text-[12px] text-[#A3A3A3]">
            Fill in every detail — Aria runs the policy engine on it the moment you save.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Merchant">
            <Input value={form.merchant} onChange={(e) => set("merchant", e.target.value)} placeholder="e.g. Delta Airlines"
              data-testid="create-expense-merchant" className={inputCls} />
          </Field>
          <Field label="Amount (USD)">
            <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)}
              placeholder="0.00" data-testid="create-expense-amount" className={`${inputCls} tabular-nums`} />
          </Field>
          <Field label="Date">
            <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)}
              data-testid="create-expense-date" className={`${inputCls} tabular-nums [color-scheme:dark]`} />
          </Field>
          <Field label="Employee">
            <Select value={form.employee_id} onValueChange={(v) => set("employee_id", v)}>
              <SelectTrigger className={selectCls} data-testid="create-expense-employee">
                <SelectValue placeholder="Pick an employee" />
              </SelectTrigger>
              <SelectContent className="max-h-64 border-[#222] bg-[#161616] text-white">
                {employees.map((emp) => <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Category">
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v, gl_code: CATEGORY_GL[v] || f.gl_code }))}>
              <SelectTrigger className={selectCls} data-testid="create-expense-category"><SelectValue /></SelectTrigger>
              <SelectContent className="border-[#222] bg-[#161616] text-white">
                {CATEGORY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="GL code">
            <Select value={form.gl_code} onValueChange={(v) => set("gl_code", v)}>
              <SelectTrigger className={selectCls} data-testid="create-expense-gl"><SelectValue /></SelectTrigger>
              <SelectContent className="border-[#222] bg-[#161616] text-white">
                {GL_OPTIONS.map((g) => <SelectItem key={g.code} value={g.code}>{g.code} — {g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          {/* attendees */}
          <div className="sm:col-span-2">
            <Field label="Attendees">
              <div className="rounded-xl bg-[#161616] p-2.5" data-testid="create-expense-attendees">
                <div className="flex flex-wrap items-center gap-1.5">
                  {attendees.map((name) => (
                    <span key={name} data-testid={`create-expense-attendee-${name.replace(/\s/g, "-")}`}
                      className="flex items-center gap-1.5 rounded-md bg-[rgba(79,142,247,0.14)] py-1 pl-1.5 pr-2 text-[11.5px] text-[#cfe0fb]">
                      <PersonAvatar name={name} size={16} />
                      {name}
                      <button onClick={() => setAttendees((a) => a.filter((n) => n !== name))}
                        data-testid={`create-expense-attendee-remove-${name.replace(/\s/g, "-")}`}
                        className="text-[#6b6b6b] hover:text-white"><X size={11} /></button>
                    </span>
                  ))}
                  <Select value="" onValueChange={addAttendee}>
                    <SelectTrigger className="h-7 w-auto gap-1.5 border-0 bg-transparent px-2 text-[11.5px] text-[#7FAEF9] hover:text-white [&>svg]:hidden"
                      data-testid="create-expense-add-attendee">
                      <UserPlus size={12} /> Add attendee
                    </SelectTrigger>
                    <SelectContent className="max-h-56 border-[#222] bg-[#161616] text-white">
                      {employees.filter((emp) => !attendees.includes(emp.name)).map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Field>
          </div>

          <Field label="Location">
            <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. San Francisco, CA"
              data-testid="create-expense-location" className={inputCls} />
          </Field>
          <Field label="Payment method">
            <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
              <SelectTrigger className={selectCls} data-testid="create-expense-payment">
                <SelectValue placeholder="How was it paid?" />
              </SelectTrigger>
              <SelectContent className="border-[#222] bg-[#161616] text-white">
                {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Tax (optional)">
            <Input type="number" min="0" step="0.01" value={form.tax} onChange={(e) => set("tax", e.target.value)}
              placeholder="0.00" data-testid="create-expense-tax" className={`${inputCls} tabular-nums`} />
          </Field>
          <Field label="Tip (optional)">
            <Input type="number" min="0" step="0.01" value={form.tip} onChange={(e) => set("tip", e.target.value)}
              placeholder="0.00" data-testid="create-expense-tip" className={`${inputCls} tabular-nums`} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Calendar event (optional)">
              <Input value={form.calendar_event} onChange={(e) => set("calendar_event", e.target.value)}
                placeholder="e.g. Client dinner — Northwind renewal" data-testid="create-expense-calendar" className={inputCls} />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Notes (optional)">
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2}
                placeholder="Anything the reviewer should know…" data-testid="create-expense-notes"
                className="resize-none border-[#2a2a2a] bg-[#161616] text-[13px] text-white placeholder:text-[#555]" />
            </Field>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className="h-9 border-[#2a2a2a] bg-transparent px-4 text-[12.5px] text-[#A3A3A3] hover:bg-[#1c1c1c] hover:text-white">
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="create-expense-submit"
            className="h-9 btn-gradient px-4 text-[13px] font-medium text-white disabled:opacity-40">
            {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Plus size={14} className="mr-1.5" />}
            Create expense
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
